import { useEffect, useState } from 'react';
import useFiltersState from '../../../../state/useFiltersState';
import ModuleFilters, { TgetFiltersList } from '../../../../components/ModuleFilters/ModuleFilters';
import { getKAMUserFilter, getOrderFilters } from '../../../../services/orders';
import errorMessage from '../../../../utils/errorMessage';
import { IKamClient } from '../../../../types/kamClient.types';
import { paymentMethodLabelsValue } from '../../../../utils/variablesToTranslate';
import useUsersState from '../../../../state/useUsersState';
import { IProductList } from '../../../../types/orders';
import { IbackofficeUserData } from '../../../../types/configuration.types';
import './OrdersFilters.scss';

const OrdersFilters = ({ orderList }: { orderList?: IProductList[] }) => {
  const { ordersFilters, setOrdersFilters } = useFiltersState();
  const { users } = useUsersState();

  const [countriesOptions, setCountriesOptions] = useState<string[]>([]);
  const [openMonthFilter, setOpenMonthFilter] = useState<boolean>(false);
  const [openPaymentMethodFilter, setOpenPaymentMethodFilter] = useState<boolean>(false);
  const [openShipmentCountryFilter, setOpenShipmentCountryFilter] = useState<boolean>(false);
  const [openAnnotationsFilter, setOpenAnnotationsFilter] = useState<boolean>(false);
  const [openKAMFilter, setOpenKAMFilter] = useState<boolean>(false);
  const [openResponsibleFilter, setOpenResponsibleFilter] = useState<boolean>(false);
  const [kamOptionsObject, setKamOptionsObject] = useState<IKamClient[]>([]);

  const ordersFilterMapping = {
    Mes: 'month',
    'Método pago': 'paymentType',
    'País de envio': 'countryDestination',
    Anotaciones: 'annotations',
    KAM: 'kam',
    Responsable: 'responsible'
  };

  const monthFilterRawOptions = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre'
  ];

  const monthToNumberMap: Map<string, string> = monthFilterRawOptions
    ? new Map(monthFilterRawOptions?.map((month, i) => [month, i + 1]))
    : new Map();

  const paymentMethodRawOptionName = paymentMethodLabelsValue.map(item => item.name);

  const paymentMethodNameValue: Map<string, string> = new Map(
    paymentMethodLabelsValue?.map(type => [type.name, type.value])
  );

  const annotationOptionsObject = {
    'Sin seriales': 'serial',
    'Sin costos': 'purchaseValue',
    'Configuraciones adicionales procesadas': 'configDone',
    'Configuraciones adicionales pendientes': 'configPending',
    'Parada en Bodega': 'temporaryStorage',
    'Pendiente por factura': 'needInvoice'
  };
  const annotationOptionsList = new Map(Object.entries(annotationOptionsObject));
  const kamFilterRawOptions = kamOptionsObject.map(item => item?.email || '');
  const kamNameToCodeMap: Map<string, string> = new Map(kamOptionsObject?.map(type => [type.email, String(type.id)]));

  const responsibleUserIds = (orderList || []).flatMap(order => order?.responsibleIds);

  const noneResponsible = {
    email: 'Sin responsables asignados',
    id: 0,
    firstName: '',
    lastName: ''
  } as IbackofficeUserData;

  const responsibleUsers = users.filter(user => responsibleUserIds.includes(user.id));

  const responsibleUserRawOptions = [noneResponsible, ...responsibleUsers].map(user => user.email);

  const responsibleUserEmailToIdMap = new Map(
    [noneResponsible, ...responsibleUsers].map(user => [user.email, String(user.id)])
  );

  const responsibleUserEmailToNameMap = new Map(
    responsibleUsers.map(user => [user.email, `${user.firstName} ${user.lastName}`])
  );

  const getOrdersFiltersList: TgetFiltersList = (formModuleFilters, handleClickFilterOption, getFilterActiveStatus) => [
    {
      title: 'Mes',
      openFunction: setOpenMonthFilter,
      openStatus: openMonthFilter,
      rawSelectOptions: formModuleFilters('Mes', monthFilterRawOptions, monthToNumberMap),
      handleChangeSelectedOption: handleClickFilterOption('month', 'value'),
      isFilterActive: getFilterActiveStatus('month')
    },
    {
      title: 'Método pago',
      openFunction: setOpenPaymentMethodFilter,
      openStatus: openPaymentMethodFilter,
      rawSelectOptions: formModuleFilters('Método pago', paymentMethodRawOptionName, paymentMethodNameValue),
      handleChangeSelectedOption: handleClickFilterOption('paymentType', 'value'),
      isFilterActive: getFilterActiveStatus('paymentType')
    },
    {
      title: 'País de envio',
      openFunction: setOpenShipmentCountryFilter,
      openStatus: openShipmentCountryFilter,
      rawSelectOptions: formModuleFilters('País de envio', countriesOptions),
      handleChangeSelectedOption: handleClickFilterOption('countryDestination', 'name'),
      isFilterActive: getFilterActiveStatus('countryDestination'),
      showSearch: true,
      searchPlaceholder: 'Buscar país de envío',
      sortAlphabetically: true
    },
    {
      title: 'Anotaciones',
      openFunction: setOpenAnnotationsFilter,
      openStatus: openAnnotationsFilter,
      rawSelectOptions: formModuleFilters('Anotaciones', Object.keys(annotationOptionsObject), annotationOptionsList),
      handleChangeSelectedOption: handleClickFilterOption('annotations', 'value'),
      isFilterActive: getFilterActiveStatus('annotations'),
      filterClass: 'annotationsOrdersClass'
    },
    {
      title: 'KAM',
      openFunction: setOpenKAMFilter,
      openStatus: openKAMFilter,
      rawSelectOptions: formModuleFilters('KAM', kamFilterRawOptions, kamNameToCodeMap),
      handleChangeSelectedOption: handleClickFilterOption('kam', 'value'),
      isFilterActive: getFilterActiveStatus('kam')
    },
    {
      title: 'Responsable',
      openFunction: setOpenResponsibleFilter,
      openStatus: openResponsibleFilter,
      rawSelectOptions: formModuleFilters('Responsable', responsibleUserRawOptions, responsibleUserEmailToIdMap).map(
        option => ({
          ...option,
          lineTitle: responsibleUserEmailToNameMap.get(option.name)
        })
      ),
      handleChangeSelectedOption: handleClickFilterOption('responsible', 'value'),
      isFilterActive: getFilterActiveStatus('responsible')
    }
  ];

  const getShipmentCountryFilterOptions = async () => {
    const response = await getOrderFilters();
    if (response?.countries) setCountriesOptions(response?.countries);
  };

  const fetchKamFilters = async () => {
    try {
      const kamFilters = await getKAMUserFilter();
      if (kamFilters) {
        setKamOptionsObject(kamFilters);
      }
    } catch (error) {
      errorMessage();
    }
  };

  useEffect(() => {
    getShipmentCountryFilterOptions();
    fetchKamFilters();
  }, []);

  return (
    <div className="ordersFilters">
      <ModuleFilters
        moduleFilters={ordersFilters}
        setModuleFilters={setOrdersFilters}
        getFiltersList={getOrdersFiltersList}
        moduleFiltersTitlesMapping={ordersFilterMapping}
        customGap={32}
        optionTextCustomClass="optionTextCustomClassBussines"
      />
    </div>
  );
};

export default OrdersFilters;
